/**
 * LessonPage Component
 *
 * Description:
 * Main container for rendering a lesson page in the educational platform. Handles loading, access control, and layout for lesson content. Fetches and manages lesson, course, and user data from Redux, and coordinates progress, transcript, and navigation between lesson activities.
 *
 * Features:
 * - Handles lesson, course, and user data fetching and synchronization.
 * - Controls access to lesson content based on user permissions.
 * - Manages progress tracking for video, quiz, homework, and coevaluation activities.
 * - Supports transcript retrieval and player time tracking.
 * - Passes relevant props to custom child components for navigation and actions.
 * - Displays loading and access control states.
 *
 * Props:
 * @param {number} idCourse - Course ID for the lesson. Required for data fetching and navigation.
 * @param {number} idLesson - Lesson ID for the lesson. Required for data fetching and navigation.
 *
 * Hooks & State:
 * - useDispatch, useSelector: Interact with Redux for global state management.
 * - format: Controls the lesson format (AUDIO/VIDEO).
 * - transcript: Stores the transcript text for the current lesson.
 * - view: Controls which tab/view is active (1: video/audio, 2: quiz, 3: activity, 4: coevaluation).
 * - isLoading: Indicates if the lesson data is still loading.
 * - actuallyIdLesson: Tracks the currently loaded lesson ID for sync with Redux.
 * - historyMedia: Stores user's media history for the lesson.
 * - videoProgress, quizProgress, homeworkProgress, coevaluationProgress: Track progress for each lesson activity.
 * - playerTime: Stores the current time in the video/audio player.
 *
 * Render Flow:
 * 1. On mount, hides the academy banner.
 * 2. Fetches course access, additional info, and lessons if not already loaded.
 * 3. Loads lesson data, status, and video files when course/lesson IDs change.
 * 4. Updates progress and transcript state as lesson activities are completed.
 * 5. Renders:
 *    - BreadCrumbs for navigation.
 *    - LessonLeft and LessonRight for main lesson content and navigation.
 *    - NoAccess if the user does not have access.
 *    - Loading while data is being fetched.
 *
 * Good Practices / Warnings:
 * - Ensure idCourse and idLesson are provided for correct data fetching.
 * - All state updates are handled via hooks and Redux actions for predictable state management.
 * - Child components receive only the props they need for clarity and maintainability.
 * - Loading and access control are handled at the top level for a consistent user experience.
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { academyDataActions } from '../../store/academy-slice';
import {
  getFormatLesson,
  getListArchivosVideo,
  getStatusLessons,
  getTranscript,
  getVideoHistory,
  loadLesson,
  setFormatLesson
} from 'store/lesson-actions';
import {
  getAccesCourse,
  getCourseAditionalInfo,
  getLessonsByCourse
} from 'store/course-actions';

import NoAccess from './NoAccess/NoAccess';
import LessonLeft from './LessonLeft/LessonLeft';
import LessonRight from './LessonRight/LessonRight';
import Loading from 'components/UI/Loading/Loading';
import BreadCrumbs from 'components/UI/BreadCrumbs/BreadCrumbs';

import classes from './LessonPage.module.css';

const url = window.location.pathname.split('/');

const LessonPage = props => {
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);
  const dataLesson = useSelector(state => state.lessonInfo);
  const dataCourse = useSelector(state => state.courseInfo);
  const dataUser = useSelector(state => state.userInfo);

  // State hooks for lesson page logic
  const [format, setFormat] = useState('');
  const [transcript, setTranscript] = useState('');
  const [view, setView] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [actuallyIdLesson, setActuallyIdLesson] = useState(0);
  const [historyMedia, setHistoryMedia] = useState('');
  const [videoProgress, setVideoProgress] = useState(0);
  const [quizProgress, setQuizProgress] = useState(0);
  const [homeworkProgress, setHomeworkProgress] = useState(0);
  const [playerTime, setPlayerTime] = useState(0);
  const [coevaluationProgress, setCoevaluationProgress] =
    useState(0);

  // Hide academy banner on mount
  useEffect(() => {
    dispatch(
      academyDataActions.showBanner({
        showBanner: false
      })
    );
  }, []);

  // Update coevaluation progress when lesson progress changes
  useEffect(() => {
    if (dataLesson.lessonProgress) {
      dataLesson.lessonProgress.map(item => {
        if (item.eventType === 5) {
          setCoevaluationProgress(item.mediaCoeval);
        }
      });
    }
  }, [dataLesson.lessonProgress]);

  // Fetch course access, additional info, and lessons when course/academy data changes
  useEffect(() => {
    if (
      dataCourse.accessCourse === null &&
      dataAcademy.data.idAcademy
    ) {
      dispatch(
        getAccesCourse(props.idCourse, dataAcademy.data.idAcademy)
      );
      dispatch(getCourseAditionalInfo(props.idCourse));
      dispatch(getLessonsByCourse(props.idCourse));
    }
  }, [dataCourse.accessCourse, dataAcademy.data]);

  // Load lesson data, status, and video files when course/lesson IDs or access change
  useEffect(() => {
    const url = window.location.pathname.split('/');
    const audioPath =
      url[3] === 'aud' || url[2] === 'aud' ? true : false;
    if (dataCourse.accessCourse) {
      (async () => {
        await setFormatLesson(
          dataUser.data.usuario,
          props.idLesson,
          audioPath ? 'AUDIO' : 'VIDEO'
        );
        setFormat(await getFormatLesson(props.idLesson));
        setHistoryMedia(
          await getVideoHistory(
            props.idLesson,
            dataAcademy.data.shortName
          )
        );
      })();
    }
    dispatch(
      loadLesson(
        props.idLesson,
        props.idCourse,
        dataAcademy.data.shortName
      )
    );
    dispatch(getStatusLessons(props.idCourse, props.idLesson));
    dispatch(getListArchivosVideo(props.idLesson, true));
  }, [
    props.idCourse,
    props.idLesson,
    dataAcademy.data,
    dataCourse.accessCourse
  ]);

  // Update loaded lesson ID when lesson/course data is available
  useEffect(() => {
    if (
      dataLesson.data.available &&
      dataCourse.accessCourse !== null &&
      dataCourse.data !== null
    ) {
      setActuallyIdLesson(dataLesson.data.idVideo);
    }
  }, [
    dataLesson.lessonProgress,
    dataLesson.data.idVideo,
    dataCourse.accessCourse
  ]);

  // Set loading state to false when the correct lesson is loaded
  useEffect(() => {
    if (props.idLesson == actuallyIdLesson) {
      setIsLoading(false);
    }
  }, [props.idLesson, actuallyIdLesson]);

  // Handlers for progress and navigation
  const handleChangeVideoStatus = state => {
    setVideoProgress(state);
  };

  const handleChangeQuizStatus = state => {
    setQuizProgress(state);
  };

  const handleChangeHomeworkStatus = state => {
    setHomeworkProgress(state);
  };

  const handleChangeCoevaluationStatus = () => {
    setCoevaluationProgress(coevaluationProgress + 1);
  };

  const changeViewHandler = idView => {
    setView(idView);
  };

  const loadLessonHandler = () => {
    setIsLoading(true);
    setActuallyIdLesson(0);
  };

  const transcriptHandler = (second, param) => {
    (async () => {
      setTranscript(
        await getTranscript(props.idLesson, second, param)
      );
    })();
  };

  const playerTimeHandler = time => {
    setPlayerTime(time);
  };

  return (
    <>
      {!isLoading ? (
        dataCourse.accessCourse ? (
          <section className={classes.lessonPageContainer}>
            {url[2] !== 'course' && url[2] !== 'lp' && (
              <BreadCrumbs
                idCourse={props.idCourse}
                title={dataLesson.data.tituloserie}
                lesson={dataLesson.data.titulo}
              />
            )}
            <aside className={classes.lessonContainer}>
              <LessonLeft
                loadLesson={loadLessonHandler}
                idCourse={props.idCourse}
                idLesson={props.idLesson}
                lessonInfo={dataLesson}
                changeView={changeViewHandler}
                view={view}
                changeVideoStatus={handleChangeVideoStatus}
                changeQuizStatus={handleChangeQuizStatus}
                changeHomeworkStatus={handleChangeHomeworkStatus}
                changeCoevaluationStatus={
                  handleChangeCoevaluationStatus
                }
                transcriptHandler={transcriptHandler}
                playerTimeHandler={playerTimeHandler}
                historyMedia={historyMedia}
                format={format}
              />
              {url[2] !== 'lp' && (
                <LessonRight
                  idCourse={props.idCourse}
                  idLesson={props.idLesson}
                  lessonInfo={dataLesson}
                  changeView={changeViewHandler}
                  view={view}
                  videoProgress={videoProgress}
                  quizProgress={quizProgress}
                  homeworkProgress={homeworkProgress}
                  coevaluationProgress={coevaluationProgress}
                  forumEnabled={dataCourse.aditionalData.forum}
                  transcript={transcript}
                  playerTime={playerTime}
                  format={format}
                />
              )}
            </aside>
          </section>
        ) : (
          <NoAccess
            idCourse={props.idCourse}
            idEmisor={dataAcademy.data.idAcademy}
            academyName={dataAcademy.data.shortName}
          />
        )
      ) : (
        <Loading />
      )}
    </>
  );
};

LessonPage.propTypes = {
  /** Course ID for the lesson */
  idCourse: PropTypes.number,
  /** Lesson ID for the lesson */
  idLesson: PropTypes.number
};

LessonPage.defaultProps = {
  idCourse: undefined,
  idLesson: undefined
};

export default LessonPage;
